
import { useState, useEffect } from "react";
import { Header } from "@/components/layout/Header";
import { Sidebar } from "@/components/layout/Sidebar";
import { AuthCheck } from "@/components/auth/AuthCheck";
import { BackgroundGradientAnimation } from "@/components/ui/background-gradient-animation";
import { AddClientModal } from "@/components/clients/AddClientModal";
import { ClientsHeader } from "@/components/clients/ClientsHeader";
import { ClientsContent } from "@/components/clients/ClientsContent";
import { useClientsData } from "@/hooks/useClientsData";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";

export default function Clients() {
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const { refreshPermissions, isBetaTier, isFounder } = usePermissionCheck();
  const {
    searchQuery,
    setSearchQuery,
    filteredClients,
    isLoading,
    fetchClients,
    removeClient
  } = useClientsData();

  // Get stored user info
  const storedAvatar = localStorage.getItem('userAvatar') || "/placeholder.svg";
  const storedName = localStorage.getItem('userName') || "Coach";

  // Refresh permissions when clients change or when finished loading
  useEffect(() => {
    if (!isLoading) {
      refreshPermissions();
    }
  }, [filteredClients.length, isLoading, refreshPermissions]);

  const handleAddClientSuccess = () => {
    setIsAddClientModalOpen(false);
    fetchClients(); // Refresh the clients list
    refreshPermissions(); // Explicitly refresh permissions after adding a client
  };

  const handleClientRemoved = (clientId: string) => {
    removeClient(clientId);
    // Explicitly refresh permissions after removing a client
    setTimeout(() => refreshPermissions(), 500); // Small delay to ensure database is updated
  };

  return (
    <AuthCheck>
      <BackgroundGradientAnimation>
        <div className="flex flex-col h-screen">
          <Header avatar={storedAvatar} name={storedName} />
          <div className="flex flex-1">
            <Sidebar />
            <main className="flex-1 p-6 space-y-6 overflow-y-auto">
              <ClientsHeader 
                clientCount={filteredClients.length}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onAddClient={() => setIsAddClientModalOpen(true)}
                showAddButton={true} // Always show the add button
                isBetaTier={isBetaTier} // Pass the beta tier status
                isFounder={isFounder} // Pass the founder status
              />

              <ClientsContent 
                clients={filteredClients}
                isLoading={isLoading}
                onAddClient={() => setIsAddClientModalOpen(true)}
                onClientRemoved={handleClientRemoved}
              />
            </main>
          </div>
        </div>

        <AddClientModal 
          isOpen={isAddClientModalOpen} 
          onClose={handleAddClientSuccess} 
        />
      </BackgroundGradientAnimation>
    </AuthCheck>
  );
}
